import React, { FC, useCallback, useState } from "react";
import { Button, ButtonProps, Checkbox, Dialog, Input, LoadingSpinner, Select, SelectItem } from "@xapp/components";

import {
    JobNimbusIntegrationInput,
    useJobNimbusAccountOptionsQuery,
    useJobNimbusIntegrationQuery,
} from "generated/graphql";

import { BasicIntegrationCard, IntegrationCardProp, IntegrationConnectionProps } from "./BasicIntegrationCard";

export interface JobNimbusConnectionProps extends IntegrationConnectionProps {
    integration: "jobnimbus";
    input: JobNimbusIntegrationInput;
}

export interface JobNimbusIntegrationCardProps extends IntegrationCardProp {
    onConnect?(props: JobNimbusConnectionProps): void;
}

/** The routing names a tenant picks. Each is a name from their own JobNimbus account. */
interface Routing {
    contactRecordTypeName?: string;
    contactStatusName?: string;
    leadSourceName?: string;
    salesRepJnid?: string;
    createJob?: boolean;
    jobRecordTypeName?: string;
    jobStatusName?: string;
}

type RoutingName = Exclude<keyof Routing, "createJob">;

const helperStyle: React.CSSProperties = { color: "#666", fontSize: "12px", marginTop: "4px" };
const errorStyle: React.CSSProperties = { color: "red", fontSize: "12px", marginTop: "4px" };

function savedRouting(token: unknown): Routing | undefined {
    const params = (token as { integrationParams?: unknown } | null | undefined)?.integrationParams;
    return params && typeof params === "object" ? (params as Routing) : undefined;
}

/**
 * JobNimbus connection card.
 *
 * Setup is two steps in one dialog: paste the API key, then "Load account" reads the tenant's
 * workflows, statuses, lead sources and users so every routing choice is a dropdown of names that
 * actually exist in their account. JobNimbus fails a create on an unknown workflow or status name.
 *
 * Editing a saved connection reads the saved routing (the API masks the key) and loads the account
 * with the saved key, so the key only needs pasting again to replace it. Nothing is fetched until
 * the user asks: the account read is gated on an explicit load, not on the dialog being open.
 */
export const JobNimbusIntegrationCard: FC<JobNimbusIntegrationCardProps> = (props) => {
    const { appId, checked, disabled, isLinked, isActive, onConnect, onDisconnect } = props;

    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [apiKey, setApiKey] = useState<string>("");
    // What the user asked to load. Null pauses the account read.
    const [loadRequest, setLoadRequest] = useState<{ apiKey?: string } | null>(null);
    // Choices made in this dialog; anything not chosen falls back to the saved value.
    const [edits, setEdits] = useState<Routing>({});

    const [savedResult] = useJobNimbusIntegrationQuery({
        pause: !isLinked || !appId,
        variables: { appId },
    });
    const saved = isLinked ? savedRouting(savedResult.data?.app?.jobnimbus?.token) : undefined;

    const [optionsResult] = useJobNimbusAccountOptionsQuery({
        pause: !loadRequest,
        variables: { appId, apiKey: loadRequest?.apiKey },
    });
    const options =
        loadRequest && !optionsResult.fetching ? optionsResult.data?.app?.jobNimbusAccountOptions : undefined;
    const loadError = loadRequest && !optionsResult.fetching ? optionsResult.error?.message : undefined;

    const value = (name: RoutingName): string => edits[name] ?? saved?.[name] ?? "";
    const createJob: boolean = edits.createJob ?? !!saved?.createJob;

    const choose = useCallback((change: Routing) => setEdits((current) => ({ ...current, ...change })), []);

    const contactWorkflows = options?.workflows.filter((w) => w.objectType === "contact") ?? [];
    const jobWorkflows = options?.workflows.filter((w) => w.objectType === "job") ?? [];
    const contactWorkflow = contactWorkflows.find((w) => w.name === value("contactRecordTypeName"));
    const jobWorkflow = jobWorkflows.find((w) => w.name === value("jobRecordTypeName"));

    const byName = (list: { name: string }[]): SelectItem[] => list.map(({ name }) => ({ label: name, value: name }));

    const resetForm = useCallback(() => {
        setApiKey("");
        setLoadRequest(null);
        setEdits({});
    }, []);

    const handleCloseDialog = useCallback(() => {
        resetForm();
        setShowDialog(false);
    }, [resetForm]);

    const handleJobNimbusClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>): void => {
            e.preventDefault();

            if (typeof checked === "boolean") {
                if (typeof props.onClick === "function") {
                    props.onClick(e);
                }
            } else {
                setShowDialog(true);
            }
        },
        [checked, props.onClick]
    );

    // Names belong to the account they were loaded from, so a new key drops the account and any
    // choices made against it rather than letting them be saved under a different account.
    const handleApiKeyChange = useCallback((val: string | number) => {
        setApiKey(String(val));
        setLoadRequest(null);
        setEdits({});
    }, []);

    const handleLoadAccount = useCallback(() => {
        setLoadRequest({ apiKey: apiKey.trim() || undefined });
    }, [apiKey]);

    const handleConnectJobNimbus = useCallback(() => {
        if (typeof onConnect === "function") {
            const optional = (name: RoutingName): string | undefined => value(name) || undefined;
            onConnect({
                integration: "jobnimbus",
                input: {
                    // Blank on an edit: the API keeps the saved key. The masked one is never sent.
                    apiKey: apiKey.trim() || undefined,
                    contactRecordTypeName: optional("contactRecordTypeName"),
                    contactStatusName: optional("contactStatusName"),
                    leadSourceName: optional("leadSourceName"),
                    salesRepJnid: optional("salesRepJnid"),
                    createJob,
                    jobRecordTypeName: createJob ? optional("jobRecordTypeName") : undefined,
                    jobStatusName: createJob ? optional("jobStatusName") : undefined,
                },
            });
            handleCloseDialog();
        }
        // value() reads edits and saved, both listed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onConnect, apiKey, createJob, edits, saved, handleCloseDialog]);

    const actions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: handleCloseDialog,
        },
    ];

    if (typeof onConnect === "function") {
        actions.push({
            label: "Connect",
            variation: "primary",
            // Only once the account has been read, so every name saved is one that exists in it.
            disabled: !options || (!apiKey.trim() && !isLinked),
            onClick: handleConnectJobNimbus,
        });
    }

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/jobnimbus/jobnimbus-logo.svg"
                text="JobNimbus"
                badge="TEST"
                href={() => "/integrations/jobnimbus"}
                onClick={handleJobNimbusClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={onDisconnect}
            />
            <Dialog active={showDialog} actions={actions} title="JobNimbus Setup" onClose={handleCloseDialog}>
                <p>
                    Each lead is matched to an existing contact by email or phone, or added as a new contact, and the
                    conversation is attached as a note. You can also open a job for every lead.
                </p>
                <p>
                    In JobNimbus, go to <strong>Settings &gt; API</strong>, create a new API key, and paste it below.
                    {isLinked && " Leave it blank to keep the key already saved."}
                </p>
                <Input label="API Key" type="password" value={apiKey} onChange={handleApiKeyChange} />
                <Button
                    data-testid="jn-load-account"
                    label="Load account"
                    variation="secondary"
                    disabled={!apiKey.trim() && !isLinked}
                    onClick={handleLoadAccount}
                />
                {loadRequest && optionsResult.fetching && <LoadingSpinner />}
                {loadError && (
                    <p data-testid="jn-error" style={errorStyle}>
                        {loadError.replace(/^\[GraphQL\]\s*/, "")}
                    </p>
                )}
                {options && (
                    <>
                        <Select
                            label="Contact Workflow"
                            fullWidth={true}
                            items={byName(contactWorkflows)}
                            value={value("contactRecordTypeName")}
                            onChange={(val) => choose({ contactRecordTypeName: String(val), contactStatusName: "" })}
                        />
                        {contactWorkflow && (
                            <Select
                                label="Contact Status"
                                fullWidth={true}
                                items={byName(contactWorkflow.statuses)}
                                value={value("contactStatusName")}
                                onChange={(val) => choose({ contactStatusName: String(val) })}
                            />
                        )}
                        <Select
                            label="Lead Source"
                            fullWidth={true}
                            items={byName(options.leadSources)}
                            value={value("leadSourceName")}
                            onChange={(val) => choose({ leadSourceName: String(val) })}
                        />
                        <Select
                            label="Sales Rep"
                            fullWidth={true}
                            items={options.users.map((user) => ({ label: user.name, value: user.jnid }))}
                            value={value("salesRepJnid")}
                            onChange={(val) => choose({ salesRepJnid: String(val) })}
                        />
                        <Checkbox
                            label="Also create a job for every lead"
                            checked={createJob}
                            onChange={(val) => choose({ createJob: val })}
                        />
                        {createJob && (
                            <>
                                <Select
                                    label="Job Workflow"
                                    fullWidth={true}
                                    items={byName(jobWorkflows)}
                                    value={value("jobRecordTypeName")}
                                    onChange={(val) => choose({ jobRecordTypeName: String(val), jobStatusName: "" })}
                                />
                                {jobWorkflow && (
                                    <Select
                                        label="Job Status"
                                        fullWidth={true}
                                        items={byName(jobWorkflow.statuses)}
                                        value={value("jobStatusName")}
                                        onChange={(val) => choose({ jobStatusName: String(val) })}
                                    />
                                )}
                            </>
                        )}
                        <p style={helperStyle}>
                            JobNimbus needs a workflow and status on every contact and job it creates, unless your
                            account applies its own defaults.
                        </p>
                    </>
                )}
            </Dialog>
        </>
    );
};
